
import { DandDFeatureSource } from "@dndModels/feature_sources/SystemFeatureSource";
import { 
  FeatureBase,
} from "@models";
import { AbilityScoreExpandedType, DataType, FeatTier, isFeatTier, isStatExpandedType } from "@dndModels/Types";

import CommonUtilities from "@utilities/common_utilities";
import { ASIBaseFeature } from "@dndModels/feature/ASIBaseFeature";
import { Selectable } from "@dndModels/feature/SystemFloatingFeature";

const common_util = CommonUtilities.getInstance();

export class Feat extends DandDFeatureSource {
  data_type: DataType = "feat";
  feature_source_type = "Feat";
  repeatable = false;
  feat_tier: FeatTier = "General";

  constructor(obj?: any) {
    super(obj, "Feat");
    if (obj) {
      if (this.feature_bases.length === 0 && obj.features && obj.features.length > 0) {
        obj.features.forEach((o: any) => {
          o.source_id = this._id;
          o.source_type = this.feature_source_type;
          const feature_base = new FeatureBase(o);
          this.feature_bases.push(feature_base);
        });
      }
      // let level = obj.level ? obj.level : 4;
      // if (level === 0 && this.name.includes("Epic Boon")) {
      //   level = 20;
      // }
      this.repeatable = obj.repeatable ? obj.repeatable : false;
      let feat_tier = obj.feat_tier && obj.feat_tier !== "Standard" ? obj.feat_tier : "General";

      if ((feat_tier === "General" || feat_tier === "Origin") && obj.special_feat_type && obj.special_feat_type === "Fighting Style") {
        feat_tier = "Fighting Style";
      }
      if (isFeatTier(feat_tier)) {
        this.feat_tier = feat_tier;
      } else {
        switch (feat_tier) {
          case "Dragonmark": case "Wild Talent":
            this.feat_tier = "Origin";
          break;
        }
        if (!this.tags.includes(feat_tier)) {
          this.tags.push(feat_tier);
        }
      }
      this._ability_scores = obj._ability_scores ? obj._ability_scores : [];
      this._selectable_ability_scores = obj._selectable_ability_scores ? obj._selectable_ability_scores : [];
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     if (this.feature_bases.length === 0 && obj.features && obj.features.length > 0) {
  //       obj.features.forEach((o: any) => {
  //         o.source_id = this._id;
  //         o.source_type = this.feature_source_type;
  //         const feature_base = new FeatureBase(o);
  //         this.feature_bases.push(feature_base);
  //       });
  //     }
  //     // let level = obj.level ? obj.level : 4;
  //     // if (level === 0 && this.name.includes("Epic Boon")) {
  //     //   level = 20;
  //     // }
  //     this.repeatable = obj.repeatable ? obj.repeatable : false;
  //     this.feat_tier = obj.feat_tier && obj.feat_tier !== "Standard" ? obj.feat_tier : "General";
  //     if ((this.feat_tier === "General" || this.feat_tier === "Origin") && obj.special_feat_type && obj.special_feat_type === "Fighting Style") {
  //       this.feat_tier = "Fighting Style";
  //     }
  //   }
  // }

  _ability_scores: AbilityScoreExpandedType[] = [];
  get ability_scores() {
    let asis = this._ability_scores;
    if (this.loaded) {
      const asi_fb = this.feature_bases.find(o => o.features.filter(o2 => o2.floating_feature instanceof ASIBaseFeature && o2.floating_feature.choices.filter(o3 => o3.asi_features.filter(o4 => o4.amount > 0).length > 0).length > 0).length > 0);
      if (asi_fb) {
        const f = asi_fb.features.find(o2 => o2.floating_feature instanceof ASIBaseFeature && o2.floating_feature.choices.filter(o3 => o3.asi_features.filter(o4 => o4.amount > 0).length > 0).length > 0);
        if (f && f.floating_feature instanceof ASIBaseFeature) {
          const ff = f.floating_feature;
          const choice = ff.choices[0];
          asis = common_util.unique_array(choice.asi_features.flatMap(o => o.options));
        }
      }
    }
    if (asis.includes("Selectable")) {
      asis = asis.filter(o => o !== "Selectable");
      asis.push(...this.selectable_ability_scores);
    }
    return asis;
  }

  _selectable_ability_scores: AbilityScoreExpandedType[] = [];
  get selectable_ability_scores(): AbilityScoreExpandedType[] {
    let asis = this._selectable_ability_scores;
    if (this.loaded) {
      const selectable_fb = this.feature_bases.find(o => o.features.filter(o2 => o2.floating_feature instanceof Selectable && o2.floating_feature.type === "Ability Score").length > 0);
      if (selectable_fb) {
        const f = selectable_fb.features.find(o2 => o2.floating_feature instanceof Selectable && o2.floating_feature.type === "Ability Score");
        if (f && f.floating_feature instanceof Selectable) {
          const ff = f.floating_feature;
          if (ff.options.length > 0) {
            const options: AbilityScoreExpandedType[] = [];
            for (const o of ff.options) {
              if (isStatExpandedType(o)) {
                options.push(o);
              }
            }
            asis = options;
          }
        }
      }
    }
    return asis;
  }

  get asi_string() {
    return this.ability_scores.join(", ");
  }

  get_detail_format() {
    const str = `${super.get_detail_format()}; Feat Type: ${this.feat_tier}`;
    return str.trim();
  }

  toDBObj(): any {
    const feature_source_obj = super.toDBObj();
    return {
      ...feature_source_obj,
      repeatable: this.repeatable,
      feat_tier: this.feat_tier
    };
  }

  toLight(): any {
    const feature_source_obj = super.toLight();
    return {
      ...feature_source_obj,
      repeatable: this.repeatable,
      feat_tier: this.feat_tier,
      _ability_scores: this.ability_scores,
      _selectable_ability_scores: this.selectable_ability_scores
    };
  }

  copy(copyMe: Feat, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.repeatable = copyMe.repeatable;
    this.feat_tier = copyMe.feat_tier;
  }

  clone(drop_ids = false): Feat {
    const new_obj = new Feat();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copy5e(copyMe: any): void {
    console.log(copyMe);
  }
}